import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';

interface PoiMarkerPopupPoi {
  id: string;
  name: string;
  brand: string | null;
  osm_key: string;
  osm_value: string;
}

interface PoiMarkerPopupProps {
  poi: PoiMarkerPopupPoi;
  categoryLabel: string;
  onSearchWithin: (poiId: string, radiusM: number) => void;
  onClose: () => void;
}

/**
 * Deliberately its own list, NOT derived from the shared `POI_RADIUS_OPTIONS_M`
 * (near-POI FilterPanel picker): `openspec/specs/poi-map-layer/spec.md` documents
 * this popup's "search within" presets as exactly 500m/1km/2km. Keep the two
 * decoupled so a FilterPanel-only preset change (e.g. adding 5km) can't silently
 * add an unspecified, untested button to this fixed-width popup.
 */
const MARKER_POPUP_RADIUS_OPTIONS_M = [500, 1000, 2000] as const;

const RADIUS_OPTIONS = MARKER_POPUP_RADIUS_OPTIONS_M.map((m) => ({
  radiusM: m,
  meters: m < 1000 ? m : undefined,
  km: m >= 1000 ? m / 1000 : undefined,
}));

export function PoiMarkerPopup({
  poi,
  categoryLabel,
  onSearchWithin,
  onClose,
}: PoiMarkerPopupProps) {
  const { t } = useTranslation('map');

  return (
    <div className="min-w-[220px] max-w-[300px] text-[13px]" data-testid="poi-popup">
      {/* Close button */}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        data-testid="poi-popup-close"
        aria-label={t('pois.popup.close')}
        onClick={onClose}
      >
        <span className="sr-only">{t('pois.popup.close')}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-3 w-3"
        >
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </Button>

      {/* POI name */}
      <p className="font-semibold text-[14px] text-foreground truncate pr-6">{poi.name}</p>

      {/* Category */}
      <p className="text-muted-foreground text-[12px] mt-0.5">{categoryLabel}</p>

      {/* Brand (conditional) */}
      {poi.brand != null ? (
        <p data-testid="poi-popup-brand" className="text-[12px] text-muted-foreground mt-0.5">
          {t('pois.popup.brand')}: {poi.brand}
        </p>
      ) : null}

      {/* Search within buttons */}
      <div className="mt-3">
        <p className="text-[11px] text-muted-foreground mb-1.5">{t('pois.popup.searchWithin')}</p>
        <div className="flex gap-1.5">
          {RADIUS_OPTIONS.map(({ radiusM, meters, km }) => (
            <Button
              key={radiusM}
              type="button"
              variant="outline"
              size="xs"
              data-testid={`poi-search-within-${radiusM}`}
              onClick={() => onSearchWithin(poi.id, radiusM)}
            >
              {meters != null
                ? t('pois.popup.radiusMeters', { meters })
                : t('pois.popup.radiusKm', { km })}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
